import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const JIMNY_HERO = "/products/jimny/731077_6f5675613e92452c8e2a59c3958e1904~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ");

// Already-native products elsewhere on the site — cross-linked here, not duplicated.
// Price/priceWas are re-fetched live from their home category on load (see useEffect below)
// so admin price changes elsewhere on the site are reflected here automatically.
const JIMNY_LINKED_PRODUCTS = [
  { name: "Suzuki Jimny Roof Rack Kits — 3 & 5 Door", price: 1895, priceFrom: true, thumb: "/products/roofracks/731077_96231b93e6fb4472a9fd2fb3e74100b9~mv2.jpg", to: "/roof-racks/jimny-roof-rack-kits-3-5-door" },
  { name: "K9 Gutter Mount Load Bars — Pair", price: 699, priceFrom: true, thumb: "/products/roofracks/731077_c516d5195a1a4bd2bfd0b1eca4e519f6~mv2.jpg", to: "/roof-racks/k9-gutter-mount-load-bars-pair" },
  { name: "Quick Pitch 1450mm Load Bars With Gutter Mount Legs", price: 699, priceFrom: true, thumb: "/products/roofracks/731077_132e8b26c9054dbe878fd38f3fedee31~mv2.jpg", to: "/roof-racks/quick-pitch-1450mm-gutter-mount" },
  { name: "Wildog Bull Bar for Suzuki Jimny", price: 2790, thumb: "/products/bullbars/731077_d05658ffb2574b66b4a2fe523c83fb2b~mv2.jpg", to: "/bull-bars/wildog-jimny" },
  { name: "Wildog Hunter Bull Bar for Suzuki Jimny", price: 2990, thumb: "/products/bullbars/731077_7832bc174549441d9e2686cc16383fe7~mv2.jpeg", to: "/bull-bars/wildog-hunter-jimny" },
  { name: "Wildog Rear Bar for Suzuki Jimny", price: 2295, priceFrom: true, thumb: "/products/bullbars/731077_eac161652ea342d881355e8c9073b193~mv2.jpg", to: "/bull-bars/wildog-rear-jimny" },
  { name: "ONCA Suzuki Jimny Drawers with Aluminium Table", price: 3899, thumb: "/products/bullbars/731077_ee5e682d4eb745549df1ea2ada679879~mv2.jpg", to: "/bull-bars/onca-jimny-drawers" },
  { name: "EEZI-AWN Galaxy (Hybrid Roof Tent)", price: 2595, thumb: "/products/rooftents/731077_9087be51bc1f401fb264738e9b523390~mv2.jpg", to: "/roof-tents/eezi-awn-galaxy" },
  { name: "Quick Pitch Aero Roof Tent — Clearance", price: 3890, priceWas: 4790, thumb: "/products/rooftents/731077_9f6dcff029df4808b3ea745996181dd8~mv2.jpg", to: "/roof-tents/quick-pitch-aero" },
  { name: "Quick Pitch Weathershade Awning — 2.0m Version", price: 1999, thumb: "/products/awnings/731077_fc31117558754a3b8f870e1803f7b1b1~mv2.jpg", to: "/awnings/quick-pitch-weathershade-2m" },
  { name: "EEZI-AWN BAT270 Awning — Grey Canvas", price: 1499, priceWas: 1799, thumb: "/products/awnings/731077_ce5fddc0ee13473d99e02b6fd237de6c~mv2.jpg", to: "/awnings/eezi-awn-bat270" },
  { name: "EEZI-AWN Swift Awning", price: 949, thumb: "/products/awnings/731077_92b179edce874f218e46e92f2a3ac8e4~mv2.jpg", to: "/awnings/eezi-awn-swift" },
  { name: "Canvas Wall Kit for EEZI-AWN Swift", price: 1295, thumb: "/products/awnings/731077_8014d5f2526647f7a37bdf1f8b02e7b1~mv2.jpg", to: "/awnings/canvas-wall-kit-swift" },
  { name: "EEZI-AWN Stainless Steel Table — Large", price: 625, thumb: "/products/roofracks/731077_423027caefae4520ad5f24558268f805~mv2.jpg", to: "/roof-racks/stainless-steel-table-large" },
];

const Card = ({ item, testid }) => (
  <Link
    to={item.to}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={item.thumb} secondary={item.gallery?.[1]?.src} alt={item.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {item.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {item.badge}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{item.name.toUpperCase()}</h3>
        <div className="flex items-end justify-between gap-3 mt-4">
          <div>
            {item.priceWas && (
              <p className="font-mono text-[10px] text-white/40 line-through">${fmt(item.priceWas)}</p>
            )}
            <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">
              {item.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
              ${fmt(item.price)}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

const POPULAR_TO = ["/roof-racks/jimny-roof-rack-kits-3-5-door", "/jimny-store/bushtech-sliding-window-jimny", "/awnings/quick-pitch-weathershade-2m"];

const CATEGORY_BY_PREFIX = {
  "/roof-racks/": "roofrack",
  "/bull-bars/": "bullbar",
  "/roof-tents/": "rooftent",
  "/awnings/": "awning",
};

function categoryAndSlugFor(to) {
  const prefix = Object.keys(CATEGORY_BY_PREFIX).find((p) => to.startsWith(p));
  if (!prefix) return null;
  return { category: CATEGORY_BY_PREFIX[prefix], slug: to.slice(prefix.length) };
}

export default function JimnyStorePage() {
  const [products, setProducts] = useState(null); // null = still loading
  const [linkedProducts, setLinkedProducts] = useState(JIMNY_LINKED_PRODUCTS);

  useEffect(() => {
    axios.get(`${API}/api/products/jimny`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  // Pull live prices for the cross-linked products so admin price edits made on their
  // home category page are reflected here automatically, instead of drifting out of sync.
  useEffect(() => {
    const categories = [...new Set(Object.values(CATEGORY_BY_PREFIX))];
    Promise.all(categories.map((cat) => axios.get(`${API}/api/products/${cat}`).then((res) => [cat, res.data])))
      .then((results) => {
        const priceBySlug = {}; // "category:slug" -> live price
        results.forEach(([cat, docs]) => {
          docs.forEach((doc) => {
            priceBySlug[`${cat}:${doc.slug}`] = doc.price;
          });
        });
        setLinkedProducts(
          JIMNY_LINKED_PRODUCTS.map((item) => {
            const ref = categoryAndSlugFor(item.to);
            if (!ref) return item;
            const livePrice = priceBySlug[`${ref.category}:${ref.slug}`];
            return livePrice != null ? { ...item, price: livePrice } : item;
          })
        );
      })
      .catch(() => {}); // keep static fallback prices if the live fetch fails
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  const nativeItems = products.map((p) => ({ ...p, to: `/jimny-store/${p.slug}` }));
  const sortedByPrice = [...nativeItems, ...linkedProducts].sort((a, b) => b.price - a.price);
  const featured = sortedByPrice.find((p) => p.to === "/jimny-store/bushtech-sliding-window-jimny");
  const allItems = [featured, ...sortedByPrice.filter((p) => p !== featured)].filter(Boolean);
  const popularItems = POPULAR_TO.map((to) => sortedByPrice.find((p) => p.to === to)).filter(Boolean);

  return (
    <div data-testid="jimny-store-page">
      <SEO
        path="/jimny-store"
        title="Suzuki Jimny Store NZ · Genuine South African Overland Kit"
        description="NZ's most serious selection of accessories for the Suzuki Jimny — Bushtech, EEZI-AWN and Wildog. Roof racks, bull bars, windows, drawers, awnings and roof tents. No Chinese replicas."
        image={JIMNY_HERO}
      />

      {/* HERO */}
      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={JIMNY_HERO} alt="Cat looking through the Bushtech sliding window on a Suzuki Jimny" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/25 via-50% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Suzuki Jimny Store</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.9] text-white hero-text-shadow max-w-[16ch]">
            SMALL 4WD.<br />
            <span className="text-[var(--brand-primary)]">SERIOUS KIT.</span>
          </h1>
          <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 max-w-xl leading-relaxed mt-8 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            We only offer superior, South African world brands for the Suzuki Jimny — genuine EEZI-AWN, Wildog, ONCA and Bushtech engineering. No rebadged Chinese replicas, no compromise.
          </p>
        </div>
      </section>

      {/* POPULAR CROSS-SELL STRIP */}
      <section className="container-x pt-16 md:pt-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="overline mb-3">— Popular Combo</p>
            <h2 className="font-display text-2xl md:text-4xl text-white leading-[0.95]">
              POPULAR WITH <span className="text-[var(--brand-primary)]">JIMNY OWNERS.</span>
            </h2>
          </div>
          <p className="text-[var(--text-secondary)] text-sm md:text-base max-w-md">
            A roof rack to carry it, a window for airflow (and pets), and shade when you get there — the three most-asked-about Jimny upgrades.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="jimny-popular-strip">
          {popularItems.map((item, i) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`jimny-popular-${i}`}
              className="group relative block overflow-hidden border border-[var(--brand-primary)]/40 hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <HoverImage primary={item.thumb} secondary={item.gallery?.[1]?.src} alt={item.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                  Popular Pick
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display text-lg text-white leading-tight">{item.name.toUpperCase()}</h3>
                  <div className="flex items-end justify-between gap-3 mt-3">
                    <p className="font-display text-xl text-[var(--brand-primary)] leading-none">
                      {item.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
                      ${fmt(item.price)}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                      View <ArrowUpRight size={12} />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="container-x py-16 md:py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Full Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              EVERYTHING FOR <span className="text-[var(--brand-primary)]">THE JIMNY.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Featured first · then high to low · Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allItems.map((item, i) => (
            <Card key={item.to} item={item} testid={`jimny-product-${i}`} />
          ))}
        </div>
      </section>
    </div>
  );
}
